import type { Booking, PaymentKind, PaymentMethod } from '../api/bookings';
import { formatAmount } from '../format/money';
import { paymentKindLabel } from './paymentWords';
import { PaymentKindValues, PaymentMethodValues } from '../generated/enums';

/**
 * Taking money against a booking — the rules the website's payment drawer and the phone's payment
 * screen share.
 *
 * <p>
 * <strong>The amount is unsigned and the kind carries the direction.</strong> The server applies the
 * sign — only `DepositRefund` is negative — so a refund cannot be recorded as income by a client that
 * gets a minus wrong. Nothing here produces or accepts a negative number.
 * </p>
 */

/**
 * Every payment kind a person may enter by hand.
 *
 * <p>
 * <strong>`DepositRefund` is excluded, and leaving it in was a real trap.</strong> Giving a deposit
 * back is not a figure a clerk types — it is what remains after the server has applied the damage
 * charges, the accessories that did not come back, and the late fee. Recording it as a payment puts
 * the money on the ledger and leaves `DepositState` at `Held` forever, because only settlement writes
 * the closing state; and settlement, when it later runs, issues its own refund row beside the
 * hand-entered one. Both were observed by driving the flow end to end. Settlement
 * (`POST /deposit/settle`, or the return counter) is the correct path.
 * </p>
 */
export const offerableKinds: readonly PaymentKind[] = PaymentKindValues.filter(
  (kind) => kind !== 'DepositRefund',
);

/**
 * How a deposit may be said to have gone back. **`Adjustment` is not offered**, and that is the
 * counter being stricter than the server: it means no money moved, which is the one thing a refund
 * is not — and nothing in the model nets a refund against a debt yet. Recorded as cash, a refund
 * paid over UPI takes money out of a drawer it was never in, so the other four must stay sayable.
 */
export const refundMethods: readonly PaymentMethod[] = PaymentMethodValues.filter(
  (method) => method !== 'Adjustment',
);

/**
 * What is outstanding for one kind of payment.
 *
 * <p>
 * Only three kinds have an amount the system already knows: the rental balance, the deposit that was
 * agreed, and the refund of what is left of it after deductions. Everything else — a late fee, a
 * damage charge, a missing brooch — is a figure a person decides at the counter, and suggesting a
 * number for those would be inventing one.
 * </p>
 */
/**
 * What a customer has paid beyond what the booking now owes — a discount given after the token, or a
 * booking cancelled before cancelling could give money back (2026-10-15). The server caps a rent
 * refund at exactly this.
 */
export function overpaid(booking: Pick<Booking, 'paidAmount' | 'invoiceTotal'>): number {
  return Math.max(0, booking.paidAmount - (booking.invoiceTotal ?? 0));
}

export function suggestedFor(booking: Booking, kind: PaymentKind): number {
  if (kind === 'RentalRefund') {
    return overpaid(booking);
  }

  if (kind === 'RentalBalance' || kind === 'Token') {
    return Math.max(0, booking.balanceDue);
  }

  if (kind === 'Deposit') {
    return booking.deposit.state === 'NotTaken' ? (booking.deposit.amount ?? 0) : 0;
  }

  if (kind === 'DepositRefund') {
    const held = booking.deposit.amount ?? 0;
    const forfeited = booking.deposit.forfeitedAmount ?? 0;
    const alreadyBack = booking.deposit.refundedAmount ?? 0;

    return Math.max(0, held - forfeited - alreadyBack);
  }

  return 0;
}

/** The amount typed, as a number the server will take — or null when it is not one. */
export function paymentAmount(typed: string): number | null {
  const trimmed = typed.trim();

  if (trimmed === '') {
    return null;
  }

  const value = Number(trimmed);

  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * How a deposit goes back unless somebody says otherwise: **the way it came in** — UPI in, UPI
 * back — because a refund recorded against the wrong instrument takes money out of a drawer or an
 * account it never reached. Cash where the deposit was never recorded as a payment, or came in by
 * a way a refund cannot be said to go (`Adjustment`).
 *
 * <p>
 * One of the owner's provisional defaults (`docs/Mobile_Redesign_Plan.md`, decision 2): the
 * settle screen still offers every way, and this only chooses which one starts ticked.
 * </p>
 */
export function depositPaidBy(booking: Pick<Booking, 'payments'>): PaymentMethod {
  const latest = [...booking.payments]
    .filter((payment) => payment.kind === 'Deposit' && payment.amount > 0)
    .sort((a, b) => b.receivedOn.localeCompare(a.receivedOn))[0];

  return latest && refundMethods.includes(latest.method) ? latest.method : 'Cash';
}

/**
 * The phone's payment screen (`docs/Mobile_Redesign_Plan.md`, step 6): the three things a counter
 * takes money for every day, as chips, and the rest behind "Something else".
 *
 * <p>
 * Together the two lists are exactly {@link offerableKinds}, so nothing the website offers is lost —
 * only ordered. The rental balance covers an extension's days too: they are part of what the booking
 * owes, so "Rental" is how the clerk takes them after Keep it longer.
 * </p>
 */
export const everydayKinds: readonly PaymentKind[] = [
  'RentalBalance',
  'Deposit',
  'AlterationCharge',
];

/**
 * The rarer kinds behind "Something else". **Not the two a booking's own flows write**: rent given
 * back is offered by itself when something is overpaid (`refundOffer`), and a cancellation charge is
 * what cancelling keeps — recorded by hand, it would count as rent paid with nothing charged for it.
 */
export const otherKinds: readonly PaymentKind[] = offerableKinds.filter(
  (kind) => !everydayKinds.includes(kind) && kind !== 'RentalRefund' && kind !== 'CancellationFee',
);

/** Whether the booking has rent to give back, and so offers "Give back" as a purpose. */
export function refundOffer(booking: Pick<Booking, 'paidAmount' | 'invoiceTotal'>): boolean {
  return overpaid(booking) > 0;
}

/**
 * Cash, UPI and card — the choices where a booking's money is still one method (the bag's token,
 * the stage dialog, settling a deposit). **`Adjustment` is never offered** — no money moves under
 * it, so it is not taking a payment. Take a payment itself is a box per account (`cash/split.ts`).
 */
export const everydayMethods: readonly PaymentMethod[] = ['Cash', 'Upi', 'Card'];

/**
 * A "what for" chip: the kind, with what is outstanding on it when the system knows — "Rental ·
 * ₹7,800 left", "Security deposit · ₹14,700" — and the kind alone when it is a figure the counter
 * decides.
 */
export function purposeLabel(booking: Booking, kind: PaymentKind, currency = ''): string {
  const name =
    kind === 'RentalBalance'
      ? 'Rental'
      : kind === 'RentalRefund'
        ? 'Give back'
        : paymentKindLabel[kind];
  const outstanding = suggestedFor(booking, kind);

  if (outstanding <= 0) {
    return name;
  }

  return `${name} · ${currency}${formatAmount(outstanding)}${kind === 'RentalBalance' ? ' left' : ''}`;
}

/**
 * The button when one payment is split across accounts — "Take ₹5,040 · 2 receipts", "Give back
 * ₹1,200" — a receipt per account, so the count says how many will print.
 */
export function takePartsLabel(
  total: number,
  receipts: number,
  currency = '',
  kind: PaymentKind = 'RentalBalance',
): string {
  if (receipts === 0) {
    return 'Enter an amount';
  }

  const verb = kind === 'RentalRefund' ? 'Give back' : 'Take';
  const many = receipts > 1 ? ` · ${receipts} receipts` : '';

  return `${verb} ${currency}${formatAmount(total)}${many}`;
}
